import React, { useState, useEffect, useRef } from 'react';

// Validate time string in HH:MM format
const isValidTime = (timeStr) => {
  const match = timeStr.match(/^(\d{2}):(\d{2})$/);
  if (!match) return false;

  const hours = parseInt(match[1], 10);
  const minutes = parseInt(match[2], 10);

  if (hours < 0 || hours > 23) return false;
  if (minutes < 0 || minutes > 59) return false;

  return true;
};

// Normalize time to HH:MM format
const normalizeTime = (timeStr) => {
  if (!timeStr) return '';
  // If already in HH:MM format
  if (/^\d{2}:\d{2}$/.test(timeStr)) return timeStr;
  // If in H:MM:SS or HH:MM:SS format, pad hour and trim seconds
  const hms = timeStr.match(/^(\d{1,2}):(\d{2})(?::\d{2})$/);
  if (hms) return `${hms[1].padStart(2, '0')}:${hms[2]}`;
  return timeStr;
};

function TimeInput({ value, onChange, required, placeholder = 'HH:MM', ...props }) {
  const [displayValue, setDisplayValue] = useState('');
  const [isValid, setIsValid] = useState(true);
  const timePickerRef = useRef(null);

  // Update display value when prop value changes
  useEffect(() => {
    setDisplayValue(normalizeTime(value));
  }, [value]);

  // Handle time picker change
  const handleTimePickerChange = (e) => {
    const timeValue = e.target.value;
    if (timeValue) {
      setDisplayValue(timeValue);
      setIsValid(true);
      onChange(timeValue);
    }
  };

  // Open time picker
  const openTimePicker = () => {
    if (timePickerRef.current) {
      timePickerRef.current.showPicker();
    }
  };

  const handleChange = (e) => {
    let input = e.target.value;

    // Allow only digits and colons
    input = input.replace(/[^\d:]/g, '');

    // Auto-add colon after hours
    if (input.length === 2 && !input.includes(':')) {
      input += ':';
    }

    // Limit length
    if (input.length > 5) {
      input = input.substring(0, 5);
    }

    setDisplayValue(input);

    // Validate and update
    if (input === '') {
      setIsValid(true);
      onChange('');
    } else if (input.length === 5 && isValidTime(input)) {
      setIsValid(true);
      onChange(input);
    } else if (input.length === 5) {
      setIsValid(false);
    } else {
      setIsValid(true); // Still typing
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    let pasted = e.clipboardData.getData('text').trim();

    // Try to parse various formats
    let parsed = '';

    // H:MM or H:MM:SS or HH:MM or HH:MM:SS
    const hmsMatch = pasted.match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?$/);
    if (hmsMatch) {
      const hours = hmsMatch[1].padStart(2, '0');
      const minutes = hmsMatch[2];
      parsed = `${hours}:${minutes}`;
    }
    // HH:MM
    else if (/^\d{2}:\d{2}$/.test(pasted)) {
      parsed = pasted;
    }
    // HH:MM:SS
    else if (/^\d{2}:\d{2}:\d{2}$/.test(pasted)) {
      parsed = pasted.substring(0, 5);
    }
    // HHMM (no separator)
    else if (/^\d{4}$/.test(pasted)) {
      parsed = `${pasted.substring(0, 2)}:${pasted.substring(2, 4)}`;
    }
    // HH.MM (dot separator)
    else if (/^\d{2}\.\d{2}$/.test(pasted)) {
      parsed = pasted.replace('.', ':');
    }

    if (parsed && isValidTime(parsed)) {
      setDisplayValue(parsed);
      setIsValid(true);
      onChange(parsed);
    } else {
      // Just set the pasted value and let user correct it
      setDisplayValue(pasted.substring(0, 5));
      setIsValid(false);
    }
  };

  const handleBlur = () => {
    // On blur, validate and update
    if (displayValue && displayValue.length === 5) {
      if (isValidTime(displayValue)) {
        setIsValid(true);
        onChange(displayValue);
      } else {
        setIsValid(false);
      }
    } else if (displayValue && displayValue.length > 0) {
      setIsValid(false);
    }
  };

  return (
    <div style={{ position: 'relative', display: 'inline-flex', alignItems: 'center', width: '100%' }}>
      <input
        type="text"
        value={displayValue}
        onChange={handleChange}
        onPaste={handlePaste}
        onBlur={handleBlur}
        placeholder={placeholder}
        required={required}
        style={{
          borderColor: !isValid ? '#e74c3c' : undefined,
          paddingRight: '30px',
          width: '100%',
          ...props.style
        }}
        {...props}
      />
      <input
        ref={timePickerRef}
        type="time"
        value={normalizeTime(value) || ''}
        onChange={handleTimePickerChange}
        style={{
          position: 'absolute',
          right: '5px',
          width: '20px',
          height: '20px',
          opacity: 0,
          cursor: 'pointer'
        }}
        tabIndex={-1}
      />
      <span
        onClick={openTimePicker}
        style={{
          position: 'absolute',
          right: '8px',
          cursor: 'pointer',
          color: '#666',
          fontSize: '14px',
          pointerEvents: 'auto'
        }}
        title="Open time picker"
      >
        🕐
      </span>
    </div>
  );
}

export default TimeInput;
